import { BaseAIService } from '../BaseAIService';
import { AIServiceOptions, AIServiceResponse, OllamaConfig } from '../types';
import { parseErrorResponse } from '../utils/ErrorHandler';

export class OllamaService extends BaseAIService {
  private config: OllamaConfig;
  
  constructor(config: OllamaConfig = {}) {
    super('ollama');
    this.config = config;
  }
  
  async generateText(options: AIServiceOptions): Promise<AIServiceResponse> {
    if (!this.config.model) {
      return {
        text: '',
        error: 'Ollama 模型未配置'
      };
    }
    
    try {
      const messages = this.createMessages(options.prompt, options.systemPrompt);
      
      // 使用Ollama的chat接口
      const url = this.config.apiUrl || 'http://localhost:11434/api/chat';
      const headers = {
        'Content-Type': 'application/json'
      };
      
      const body = {
        model: this.config.model,
        messages: messages.map(m => ({
          role: m.role,
          content: m.content
        })),
        stream: !!options.onUpdate,
        options: {
          temperature: options.temperature ?? this.config.temperature ?? 0.7,
          top_p: options.topP ?? this.config.topP ?? 1,
          num_predict: options.maxTokens || this.config.maxTokens
        }
      };
      
      // 如果有实时更新回调，使用流式响应
      if (options.onUpdate) {
        let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
        let accumulatedText = '';
        
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            signal: options.abortSignal
          });
          
          if (!response.ok) {
            throw await parseErrorResponse(this.provider, response);
          }
          
          // 确保response.body不为null
          if (!response.body) {
            throw new Error('Ollama响应体为空');
          }
          
          reader = response.body.getReader();
          
          // 处理流式响应
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            // 解码并处理接收到的数据块
            const chunk = new TextDecoder().decode(value);
            
            try {
              // 解析返回的JSON
              const data = JSON.parse(chunk);
              // console.log('Ollama 流式数据:', JSON.stringify(data));
              
              // 从响应中提取文本
              const text = data.response || '';
              
              // 追加到累积的响应
              accumulatedText += text;
              
              // 通知累积的响应
              await options.onUpdate(text);
            } catch (parseError) {
              console.error('解析Ollama响应出错:', parseError);
            }
          }
          
          return { text: accumulatedText };
        } finally {
          reader = null;
        }
      } else {
        // 非流式响应
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: options.abortSignal
        });
        
        if (!response.ok) {
          throw await parseErrorResponse(this.provider, response);
        }
        
        const data = await response.json();
        return {
          text: data.message?.content || data.response || ''
        };
      }
    } catch (error) {
      return this.handleError(error);
    }
  }
} 